import { apiClient } from "@/services/apiClient";

/**
 * Live posting capabilities for a client's connected TikTok creator.
 *
 * TikTok's Content Sharing Guidelines require these to be fetched fresh
 * every time the consent screen opens — privacy options, interaction
 * settings and the daily post quota all change between posts. Never
 * cache this response.
 */
export interface TikTokCreatorInfo {
  nickname: string | null;
  username: string | null;
  avatarUrl: string | null;
  privacyLevelOptions: TikTokPrivacyLevel[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number | null;
}

export type TikTokPrivacyLevel =
  | "PUBLIC_TO_EVERYONE"
  | "MUTUAL_FOLLOW_FRIENDS"
  | "FOLLOWER_OF_CREATOR"
  | "SELF_ONLY";

/** Labels TikTok requires us to show for each privacy level. */
export const TIKTOK_PRIVACY_LABELS: Record<TikTokPrivacyLevel, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only you",
};

/** The consent selections that travel with a TikTok publish request. */
export interface TikTokPostOptions {
  /**
   * The caption as the user left it on the consent screen. TikTok's UX
   * guidelines require the title to be editable before posting, so this may
   * differ from the post's stored caption and is what actually gets sent.
   */
  title: string;
  privacyLevel: TikTokPrivacyLevel;
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  /** "Your brand" — labels the post as Promotional content */
  brandOrganicToggle: boolean;
  /** "Branded content" — labels the post as Paid partnership */
  brandContentToggle: boolean;
}

export class TikTokCreatorInfoError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "TikTokCreatorInfoError";
    this.code = code;
  }
}

export interface TikTokCreatorInfoResult {
  creator: TikTokCreatorInfo;
  /**
   * False until TikTok approves the Content Posting API audit. While false
   * the video goes to the creator's TikTok drafts rather than live, and the
   * consent screen has to say so.
   */
  directPostEnabled: boolean;
}

export const fetchTikTokCreatorInfo = async (
  clientId: string
): Promise<TikTokCreatorInfoResult> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      creator: TikTokCreatorInfo;
      directPostEnabled: boolean;
    }>(`/api/social-media/tiktok/creator-info/${clientId}`);

    return {
      creator: response.creator,
      directPostEnabled: response.directPostEnabled ?? false,
    };
  } catch (error: any) {
    // apiClient's response interceptor converts axios errors into plain
    // Error objects built from the body's `error` field, so `error.response`
    // is gone by the time we get here and `error.message` is the human text
    // the route put in `error`.
    throw new TikTokCreatorInfoError(
      error?.message || "Could not reach TikTok to check posting options",
      "creator_info_unavailable"
    );
  }
};
